/**
 * atmosphere — Atlas's ONE atmosphere (2026-09-27, the coordinator's realism review, realism-additions §0.2).
 *
 * The engine's `EARTH_ATMOSPHERE` is Hillaire 2020's reference table, and its aerosol column is all but empty:
 * Mie extinction 0.00444/km over a 1.2 km scale height is an optical depth of 0.005 — a stratospheric,
 * "no aerosol" sky (a 700 km visibility). Real clear days carry an aerosol optical depth of 0.05–0.2 at 550 nm
 * (a 30–100 km visibility), and that aerosol is what fills the diffuse sky to the 15–25 klx a lux meter reads
 * under a high sun, lifts the zenith from the Rayleigh dome's ~1.5 kcd/m² toward the 3–5 kcd/m² of a real
 * one, whitens the horizon and grows the aureole. Until the multiple-scattering bake was honest (the 4π fix)
 * the missing aerosol was hidden by a Ψ_ms 12.6× too strong; now it is derived, not tuned:
 *
 *   Koschmieder: V = 3.912 / β_ext(sea level)   →   β_Mie = 3.912 / V − β_Rayleigh(photopic, 0.0136/km)
 *
 * with a single-scattering albedo of 0.9 (continental aerosol). Every consumer — the drawn sky (SkyModule), the
 * CPU sampler the key light / hemisphere / fog / exposure read (sky_sampler), the harnesses — takes THIS
 * config so the sky that is drawn and the light that is cast never disagree (photometry's ONE TABLE law).
 * The biome fog (lib/weather) is a separate, visibility-derived layer on top; both are written in visibility.
 */
import { createAtmosphereConfig, type AtmosphereConfig } from 'enginex/render/sky/AtmosphereConfig.js';

/**
 * Sea-level meteorological visibility of Atlas's baseline clear air, km (a clean continental day).
 * 50 (was 40 — sky/post P2, 2026-09-27, THE NOON LOOK QUESTION): LOOKED at a settled WebGPU med noon with the sky at
 * 30–60° (shots p2-sky-2026-09-27/webgpu-med*-noon-*): a pale, washed steel blue — not overcast, but short of a clear
 * day. Measured: most of the grey was NOT the column — the ground-layer height fog mixed its horizon colour into the
 * upward sky (fixed: fog_sky.heightFogDensity, THE VISIBILITY LAW for the ground layer) and a seeded 'clear' weather
 * row put a 1.3 km FogExp2 back (fixed: profileFogExcess). The column's own share, through three's AgX at the curve's
 * noon exposure (CPU, the 45° side sky): V 40 → sRGB (85,105,134), V 50 → (79,101,131), V 60 → (75,98,130);
 * linear b/r 2.87 → 3.15 → 3.39. 50 km deepens the dome visibly while every photometric band holds: diffuse 15.7 klx
 * (lighting-physical's 14–26 band; 60 km would sit at its 14.2 floor), zenith ≈ 2.9 kcd/m² (Kittler T_L ≈ 2.7),
 * noon key through the air ≈ 92 klx. The asymmetry stays 0.7 (the continental measurement — Hillaire's 0.8 starves
 * the side sky). Never the aerosol-free table.
 */
export const CLEAR_AIR_VISIBILITY_KM = 50;
/** Photopic Rayleigh extinction at sea level, /km (the G channel of Hillaire's table, 0.013558). */
const RAYLEIGH_SEA_LEVEL_PER_KM = 0.013558;
/** Continental aerosol single-scattering albedo (soot-darkened dust and sulphate, ~0.85–0.95). */
const AEROSOL_ALBEDO = 0.9;
/**
 * Henyey-Greenstein asymmetry of the bulk continental aerosol at 550 nm (Dubovik et al. 2002: 0.65–0.72 for urban,
 * biomass and dust). Hillaire's 0.8 is a fog/cloud droplet's — it piles the aerosol's light into the aureole and
 * starves the side sky, which is what kept the zenith and the anti-sun horizon dark against a real clear sky.
 */
const AEROSOL_ASYMMETRY = 0.7;

/** Mie extinction at sea level for a visibility, /km (Koschmieder, 2 % contrast). */
export function mieExtinctionForVisibility(visibilityKm: number): number {
  return Math.max(0, 3.912 / Math.max(1, visibilityKm) - RAYLEIGH_SEA_LEVEL_PER_KM);
}

const mieExt = mieExtinctionForVisibility(CLEAR_AIR_VISIBILITY_KM);
const mieSca = mieExt * AEROSOL_ALBEDO;

/**
 * Ozone's Chappuis absorption per channel, /km at the layer's peak (2026-10-06, the sunset-realism work). Hillaire's
 * table takes Bruneton's wavelengths — red 680 nm, where the Chappuis band is on its tail (0.65·10⁻³ against green's
 * 1.881·10⁻³) — but the display's red channel is the sRGB red band, centred near 600–610 nm, at the band's PEAK
 * (σ(600 nm) ≈ 5·10⁻²¹ cm² against 3.3·10⁻²¹ at 550 nm). The red channel's absorption is therefore taken at least
 * the green's. It changes nothing by day (a vertical ozone column is τ ≈ 0.03 in red) and everything in twilight,
 * when the light reaching the zenith grazes hundreds of km through the ozone layer: with red on the band's tail
 * the twilight zenith came out PURPLE (sun −6°: linear (0.64, 0.37, 1)); with it raised it is the blue of the
 * reference photographs (sun −4°: (0.32, 0.40, 1) against the photos' (0.29, 0.41, 1)) — the Hulburt effect, ozone's
 * reason the twilight sky is blue at all. Measured against the band-weighted guess (R 2.3, G 1.5, B 0.2 ·10⁻³), which
 * yellowed every twilight horizon past the photographs. Records: docs/research/2026-10-06-sunset-realism/.
 */
const OZONE_PER_KM = { x: 0.001881, y: 0.001881, z: 0.000085 };

/**
 * THE BACKGROUND AEROSOL (2026-10-06, the sunset-realism work; the engine's `mieBackgroundFraction`): 3 % of the
 * sea-level aerosol in a thin layer with an 8 km scale height — ~1.6·10⁻⁴/km at 20 km, the stratosphere's measured
 * background, and ~2·10⁻³/km at 5 km. The 1.2 km boundary layer alone holds nothing above a few km, and above a few km
 * is the only air still in sunlight after sunset: without it the sky over the set sun was its multiple scattering —
 * a grey-mauve (sun −4°, linear (1, 0.69, 0.74)) where the photographs show an orange arch; with it (1, 0.37, 0.29) at
 * 13× the zenith (the photographs: ≥ 6× — their top of frame is not the zenith and their horizon band clips). The sea-
 * level density — CLEAR_AIR_VISIBILITY_KM — is unchanged; the column grows 17 %: the noon zenith +8 %, the direct sun −2 %
 * (lighting-physical's bands hold).
 */
const BACKGROUND_AEROSOL_FRACTION = 0.03;
const BACKGROUND_AEROSOL_SCALE_HEIGHT_KM = 8;

/** The atmosphere Atlas draws and lights by. */
export const ATLAS_ATMOSPHERE: AtmosphereConfig = createAtmosphereConfig({
  mieExtinction: { x: mieExt, y: mieExt, z: mieExt },
  mieScattering: { x: mieSca, y: mieSca, z: mieSca },
  miePhaseG: AEROSOL_ASYMMETRY,
  ozoneExtinction: OZONE_PER_KM,
  mieBackgroundFraction: BACKGROUND_AEROSOL_FRACTION,
  mieBackgroundScaleHeight: BACKGROUND_AEROSOL_SCALE_HEIGHT_KM,
});
